import { ReactNode, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import {
  clearSession,
  getSession,
  setSession,
  setToken,
} from "../lib/auth";
import { verifyTOTP } from "../lib/totp";
import {
  LoginMustEnrollResponse,
  LoginNeedsTOTPResponse,
  LoginResponse,
  LoginResult,
} from "../types";

const LOGIN_URL = "/admin/v1/auth/login";

function isNeedsTOTP(r: LoginResult): r is LoginNeedsTOTPResponse {
  return (r as LoginNeedsTOTPResponse).needs_totp === true;
}
function isMustEnroll(r: LoginResult): r is LoginMustEnrollResponse {
  return (r as LoginMustEnrollResponse).must_enroll === true;
}

export function TokenGate({ children }: { children: ReactNode }) {
  const [hasSession, setHasSession] = useState<boolean>(() => !!getSession());
  const navigate = useNavigate();

  useEffect(() => {
    const onClear = () => setHasSession(false);
    window.addEventListener("admin-token-cleared", onClear);
    return () => window.removeEventListener("admin-token-cleared", onClear);
  }, []);

  if (hasSession) return <>{children}</>;

  // Após login bem-sucedido: marca sessão e força ir pro dashboard
  // (independente da URL anterior — útil quando a sessão expirou numa
  // sub-rota como /security ou /sites/123).
  return (
    <LoginForm
      onAuthenticated={() => {
        setHasSession(true);
        navigate("/overview", { replace: true });
      }}
    />
  );
}

export const LoginGate = TokenGate;

function LoginForm({ onAuthenticated }: { onAuthenticated: () => void }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showRawToken, setShowRawToken] = useState(false);
  const [rawToken, setRawToken] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // TOTP step. Quando `pendingToken` está setado, mostramos o input do
  // segundo fator em vez do form de email/senha.
  const [pendingToken, setPendingToken] = useState<string | null>(null);
  const [totpCode, setTotpCode] = useState("");

  async function submitLogin(e: React.FormEvent) {
    e.preventDefault();
    if (!email || !password) return;
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(LOGIN_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password }),
      });
      if (!res.ok) {
        if (res.status === 401) {
          setError("Email ou senha inválidos.");
        } else {
          setError(`Falha no login (HTTP ${res.status}).`);
        }
        return;
      }
      const body = (await res.json()) as LoginResult;
      if (isNeedsTOTP(body)) {
        // Senha bateu — agora pede o segundo fator.
        setPendingToken(body.pending_token);
        return;
      }
      if (isMustEnroll(body)) {
        // Master sem TOTP: persiste session enrollment-only e libera.
        // O Layout/Routes detectam must_enroll e redirecionam pra /security.
        setSession({
          token: body.token,
          user: {
            id: body.user.id,
            email: body.user.email,
            name: body.user.name,
            role: body.user.role,
            active: body.user.active,
          },
          expires_at: body.expires_at,
          must_enroll: true,
        });
        onAuthenticated();
        return;
      }
      persistSession(body, onAuthenticated);
    } catch {
      setError("Não foi possível contatar o servidor.");
    } finally {
      setLoading(false);
    }
  }

  async function submitTOTP(e: React.FormEvent) {
    e.preventDefault();
    if (!pendingToken || !totpCode) return;
    setLoading(true);
    setError(null);
    try {
      const body = await verifyTOTP(pendingToken, totpCode.trim());
      persistSession(body, onAuthenticated);
    } catch (err) {
      const e = err as { code?: string; message?: string };
      if (e.code === "PENDING_EXPIRED") {
        setError("Sessão de login expirou. Faça login de novo.");
        setPendingToken(null);
        setTotpCode("");
      } else if (e.code === "TOO_MANY_ATTEMPTS") {
        setError("Muitas tentativas — comece de novo.");
        setPendingToken(null);
        setTotpCode("");
      } else if (e.code === "INVALID_TOTP") {
        setError("Código inválido.");
        setTotpCode("");
      } else {
        setError(e.message || "Falha ao validar código.");
      }
    } finally {
      setLoading(false);
    }
  }

  function submitRawToken(e: React.FormEvent) {
    e.preventDefault();
    const trimmed = rawToken.trim();
    if (!trimmed) return;
    setToken(trimmed);
    onAuthenticated();
  }

  function persistSession(body: LoginResponse, done: () => void) {
    setSession({
      token: body.token,
      user: {
        id: body.user.id,
        email: body.user.email,
        name: body.user.name,
        role: body.user.role,
        active: body.user.active,
      },
      expires_at: body.expires_at,
    });
    done();
  }

  return (
    <div
      style={{
        display: "flex",
        minHeight: "100vh",
        alignItems: "center",
        justifyContent: "center",
        background: "var(--bg)",
        padding: 16,
      }}
    >
      <div style={{ width: "100%", maxWidth: 400, display: "flex", flexDirection: "column", gap: 12 }}>
        {/* Login card OR TOTP step */}
        <form
          onSubmit={pendingToken ? submitTOTP : submitLogin}
          style={{
            background: "var(--card)",
            border: "1px solid var(--border)",
            borderRadius: 14,
            padding: "32px 28px 28px",
            boxShadow: "0 4px 24px rgba(0,0,0,0.18)",
          }}
        >
          {/* Logo */}
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 10, marginBottom: 28 }}>
            <MELogo />
            <span
              style={{
                fontSize: 10,
                fontWeight: 600,
                letterSpacing: "0.18em",
                textTransform: "uppercase",
                color: "var(--muted-2)",
              }}
            >
              Observabilidade WordPress
            </span>
          </div>

          {pendingToken ? (
            <>
              <p style={{ fontSize: 13, color: "var(--muted)", marginBottom: 6 }}>
                Senha ok. Agora digite o código do app autenticador
                (Google&nbsp;Authenticator, Authy, 1Password, etc.).
              </p>
              <p style={{ fontSize: 12, color: "var(--muted-2)", marginBottom: 20 }}>
                Sem o celular? Use um dos seus <em>backup codes</em> (formato
                XXXX-XXXX-XX) — cada um vale uma vez.
              </p>

              <label
                style={{
                  display: "block",
                  fontSize: 11,
                  fontWeight: 600,
                  letterSpacing: "0.08em",
                  textTransform: "uppercase",
                  color: "var(--muted)",
                  marginBottom: 6,
                }}
              >
                Código
              </label>
              <input
                type="text"
                autoFocus
                required
                autoComplete="one-time-code"
                inputMode="text"
                className="input"
                style={{
                  width: "100%",
                  boxSizing: "border-box",
                  fontFamily: "JetBrains Mono, monospace",
                  letterSpacing: "0.15em",
                  textAlign: "center",
                  fontSize: 18,
                }}
                placeholder="123456"
                value={totpCode}
                onChange={(e) => setTotpCode(e.target.value)}
              />

              {error ? (
                <div
                  style={{
                    marginTop: 14,
                    padding: "10px 12px",
                    borderRadius: 8,
                    background: "color-mix(in srgb, var(--err-500) 12%, transparent)",
                    border: "1px solid color-mix(in srgb, var(--err-500) 30%, transparent)",
                    fontSize: 13,
                    color: "var(--err-500)",
                  }}
                >
                  {error}
                </div>
              ) : null}

              <button
                type="submit"
                className="btn-primary"
                disabled={loading}
                style={{ width: "100%", justifyContent: "center", marginTop: 20 }}
              >
                {loading ? "Verificando…" : "Verificar código"}
              </button>
              <button
                type="button"
                onClick={() => {
                  setPendingToken(null);
                  setTotpCode("");
                  setError(null);
                }}
                style={{
                  width: "100%",
                  background: "transparent",
                  border: "none",
                  cursor: "pointer",
                  fontSize: 12,
                  color: "var(--muted-2)",
                  padding: "8px 0 0",
                }}
              >
                ← voltar pro login
              </button>
            </>
          ) : (
            <>
              <p style={{ fontSize: 13, color: "var(--muted)", marginBottom: 20 }}>
                Entre com seu email e senha do painel.
              </p>

              <label
                style={{
                  display: "block",
                  fontSize: 11,
                  fontWeight: 600,
                  letterSpacing: "0.08em",
                  textTransform: "uppercase",
                  color: "var(--muted)",
                  marginBottom: 6,
                }}
              >
                Email
              </label>
              <input
                type="email"
                autoFocus
                required
                autoComplete="username"
                className="input"
                style={{ width: "100%", boxSizing: "border-box" }}
                placeholder="voce@empresa.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />

              <label
                style={{
                  display: "block",
                  fontSize: 11,
                  fontWeight: 600,
                  letterSpacing: "0.08em",
                  textTransform: "uppercase",
                  color: "var(--muted)",
                  marginBottom: 6,
                  marginTop: 16,
                }}
              >
                Senha
              </label>
              <input
                type="password"
                required
                autoComplete="current-password"
                className="input"
                style={{ width: "100%", boxSizing: "border-box" }}
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />

              {error ? (
                <div
                  style={{
                    marginTop: 14,
                    padding: "10px 12px",
                    borderRadius: 8,
                    background: "color-mix(in srgb, var(--err-500) 12%, transparent)",
                    border: "1px solid color-mix(in srgb, var(--err-500) 30%, transparent)",
                    fontSize: 13,
                    color: "var(--err-500)",
                  }}
                >
                  {error}
                </div>
              ) : null}

              <button
                type="submit"
                className="btn-primary"
                disabled={loading}
                style={{ width: "100%", justifyContent: "center", marginTop: 20 }}
              >
                {loading ? "Entrando…" : "Entrar"}
              </button>
            </>
          )}
        </form>

        {/* ADMIN_TOKEN drawer */}
        <div
          style={{
            background: "var(--surface)",
            border: "1px dashed var(--border-2)",
            borderRadius: 10,
            padding: 14,
            fontSize: 12,
          }}
        >
          <button
            type="button"
            onClick={() => setShowRawToken((v) => !v)}
            style={{
              background: "transparent",
              border: "none",
              cursor: "pointer",
              color: "var(--muted-2)",
              fontSize: 12,
              padding: 0,
              textAlign: "left",
            }}
            onMouseEnter={(e) => {
              (e.currentTarget as HTMLElement).style.color = "var(--muted)";
            }}
            onMouseLeave={(e) => {
              (e.currentTarget as HTMLElement).style.color = "var(--muted-2)";
            }}
          >
            {showRawToken ? "▾" : "▸"} Usar ADMIN_TOKEN (operador / devs)
          </button>

          {showRawToken ? (
            <form onSubmit={submitRawToken} style={{ marginTop: 12, display: "flex", flexDirection: "column", gap: 10 }}>
              <p style={{ fontSize: 12, color: "var(--muted)", margin: 0 }}>
                Cola o <code style={{ fontFamily: "JetBrains Mono, monospace", color: "var(--brand-500)" }}>ADMIN_TOKEN</code> do servidor — útil em scripts ou quando o usuário inicial ainda não foi criado.
              </p>
              <input
                type="password"
                autoComplete="off"
                className="input"
                style={{ width: "100%", boxSizing: "border-box", fontFamily: "JetBrains Mono, monospace" }}
                placeholder="bearer token"
                value={rawToken}
                onChange={(e) => setRawToken(e.target.value)}
              />
              <button
                type="submit"
                className="btn-secondary"
                style={{ width: "100%", justifyContent: "center" }}
              >
                Entrar com token
              </button>
              <button
                type="button"
                onClick={() => {
                  clearSession();
                  setRawToken("");
                  setError(null);
                }}
                style={{
                  background: "transparent",
                  border: "none",
                  cursor: "pointer",
                  fontSize: 11,
                  color: "var(--muted-2)",
                  padding: 0,
                  textAlign: "left",
                }}
                onMouseEnter={(e) => {
                  (e.currentTarget as HTMLElement).style.color = "var(--muted)";
                }}
                onMouseLeave={(e) => {
                  (e.currentTarget as HTMLElement).style.color = "var(--muted-2)";
                }}
              >
                limpar sessão local
              </button>
            </form>
          ) : null}
        </div>
      </div>
    </div>
  );
}

function MELogo() {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
      <svg width={32} height={32} viewBox="0 0 32 32" fill="none" style={{ flexShrink: 0 }}>
        <g fill="var(--brand-500)">
          {([0, 45, 90, 135, 180, 225, 270, 315] as number[]).map((a, i) => (
            <rect
              key={i}
              x="14.5"
              y="0.5"
              width="3"
              height="5"
              rx="0.6"
              transform={`rotate(${a} 16 16)`}
            />
          ))}
        </g>
        <circle cx="16" cy="16" r="10.5" fill="var(--brand-500)" />
        <circle cx="16" cy="16" r="4" fill="var(--bg, #0a0a0a)" />
        <circle cx="24" cy="9" r="2.4" fill="var(--accent-500)" />
        <circle cx="24" cy="9" r="1" fill="var(--cyan-engrene)" />
      </svg>
      <div style={{ display: "flex", flexDirection: "column", lineHeight: 1 }}>
        <span
          style={{
            fontFamily: "Montserrat, system-ui, sans-serif",
            fontWeight: 700,
            fontSize: 18,
            letterSpacing: "-0.02em",
            color: "var(--text)",
          }}
        >
          monitora
        </span>
        <span
          style={{
            fontFamily: "Montserrat, system-ui, sans-serif",
            fontWeight: 500,
            fontSize: 10,
            letterSpacing: "0.18em",
            textTransform: "uppercase",
            color: "var(--muted)",
          }}
        >
          engrene
        </span>
      </div>
    </div>
  );
}
